import React, { useEffect, useRef, useState } from 'react';
import { Button, FormControl, InputLabel, NativeSelect, TextField } from '@material-ui/core';
import { QUEUE_TYPES } from '../../../services/DispatcherQueue';
import { useStyles } from './DeliveryAlerts.styles';

const humanize = (value) => value.replace(/_/g, ' ');
const display = (value) => value == null ? 'Unavailable' :
  typeof value === 'boolean' ? value ? 'Yes' : 'No' : String(value);

function EvidenceFields({ value }) {
  const classes = useStyles();
  return <dl className={classes.fields}>{Object.entries(value || {}).map(([key, entry]) =>
    <React.Fragment key={key}><dt>{humanize(key)}</dt><dd>
      {entry && typeof entry === 'object' ? <EvidenceFields value={entry} /> : display(entry)}
    </dd></React.Fragment>)}</dl>;
}

function AlertDetail({ issue, queue, onSeen }) {
  const classes = useStyles();
  const [state, setState] = useState({ status: 'loading', data: null });
  const alive = useRef(false);
  const busy = useRef(false);
  const callbacks = useRef({ queue, onSeen });
  callbacks.current = { queue, onSeen };
  const load = async (acknowledge = false) => {
    if (busy.current) return;
    busy.current = true;
    setState((old) => ({ ...old, status: 'loading' }));
    try {
      const data = await callbacks.current.queue.detail(issue, acknowledge);
      if (alive.current) {
        setState({ status: 'success', data });
        if (acknowledge) callbacks.current.onSeen(data);
      }
    } catch (error) {
      if (alive.current) setState((old) => ({ ...old, status: 'error' }));
    } finally { busy.current = false; }
  };
  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
    // One retained-detail request per selected alert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const data = state.data;
  return <article className={classes.section}>
    <h3 className={classes.sectionTitle}>{QUEUE_TYPES[issue.event_type]} · {issue.alert_id}</h3>
    {state.status === 'loading' && <div role="status">Loading alert…</div>}
    {state.status === 'error' && <div role="status">Alert unavailable. <Button size="small" onClick={() => load()}>Retry alert</Button></div>}
    {data && <>
      <EvidenceFields value={{
        status: data.resolved_at ? 'Resolved' : 'Open',
        severity: data.severity || issue.severity,
        opened_at: data.opened_at || data.received_at,
        updated_at: data.updated_at,
        acknowledged_at: data.acknowledged_at, acknowledged_by: data.acknowledged_by,
        resolved_at: data.resolved_at,
        ...(issue.source === 'doordash' ? {
          category: data.category, message: data.message, attempt_id: data.attempt_id,
          provider_reason: data.provider_reason, provider_event_at: data.provider_event_at,
          resolution: data.resolution,
        } : { assignment_id: data.assignment_id }),
      }} />
      <details><summary>Diagnostic evidence</summary><EvidenceFields value={data.evidence || issue.evidence || { evidence: null }} /></details>
      {!data.acknowledged_at && <Button size="small" disabled={state.status === 'loading'} onClick={() => load(true)}>Mark seen</Button>}
      {data.acknowledged_at && <span className={classes.secondary}>Seen</span>}
    </>}
  </article>;
}

function OrderDetails({ group, queue }) {
  const classes = useStyles();
  const [state, setState] = useState({ status: 'loading', data: null });
  const [retry, setRetry] = useState(0);
  const callbacks = useRef(queue);
  callbacks.current = queue;
  useEffect(() => {
    let alive = true;
    setState({ status: 'loading', data: null });
    Promise.resolve().then(() => callbacks.current.order(group.issues[0])).then((data) => {
      if (alive) setState({ status: data ? 'success' : 'error', data });
    }, () => { if (alive) setState({ status: 'error', data: null }); });
    return () => { alive = false; };
  }, [group, retry]);
  const data = state.data;
  return <section className={classes.section}>
    <h3 className={classes.sectionTitle}>Order details · read only</h3>
    {state.status === 'loading' && <div role="status">Loading order…</div>}
    {state.status === 'error' && <div role="status">Order details unavailable. <Button size="small" onClick={() => setRetry((n) => n + 1)}>Retry details</Button></div>}
    {data && <EvidenceFields value={{ order_id: group.order_id, status: data.status,
      pickup: data.pickup?.name, pickup_address: data.pickup?.address,
      dropoff_address: data.dropoff?.address }} />}
  </section>;
}

export default function QueueContent({ queue }) {
  const classes = useStyles();
  const { data, status, filters, businesses } = queue;
  const [selected, setSelected] = useState(null);
  const [ids, setIds] = useState({ order_id: '', restaurant_id: '' });
  const [invalid, setInvalid] = useState(false);
  const [seen, setSeen] = useState({});
  const back = useRef(null);
  const scopeEpoch = data?.scope_epoch;
  useEffect(() => { setSelected(null); setSeen({}); }, [filters, scopeEpoch, status]);
  useEffect(() => {
    if (selected && back.current) back.current.focus();
  }, [selected]);
  const change = (key, value) => queue.setFilters({ ...filters, [key]: value, page: 1 });
  const select = (key, label, options) => <FormControl className={classes.filter} key={key}>
    <InputLabel shrink htmlFor={`queue-${key}`}>{label}</InputLabel>
    <NativeSelect value={filters[key] || ''} onChange={(event) => change(key, event.target.value)}
      inputProps={{ id: `queue-${key}`, 'aria-label': `Alert ${label.toLowerCase()} filter` }}>
      <option value="">{key === 'business_id' ? 'All authorized businesses' : 'All'}</option>
      {options.map(([value, name]) => <option value={value} key={value}>{name}</option>)}
    </NativeSelect>
  </FormControl>;
  if (status === 'forbidden') return <div className={classes.status} role="alert">Alert access unavailable.</div>;
  if (selected && data) return <div className={classes.scroll}>
    <Button ref={back} size="small" onClick={() => {
      setSelected(null);
      requestAnimationFrame(() => {
        const orderButton = document.querySelector(`[aria-label="Open order ${selected.order_id}"]`);
        if (orderButton) orderButton.focus();
      });
    }}>Back to alerts</Button>
    <h3 className={classes.section}>Order #{selected.order_id} · {queue.name(selected.issues[0])}</h3>
    <OrderDetails group={selected} queue={queue} />
    {selected.issues.map((issue) => <AlertDetail key={issue.alert_id} issue={issue} queue={queue}
      onSeen={(detail) => setSeen((old) => ({ ...old, [detail.alert_id]: detail.acknowledged_at }))} />)}
  </div>;
  return <div className={classes.scroll}>
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      {select('business_id', 'Business', businesses.map((b) => [String(b.id), b.name]))}
      {select('event_type', 'Problem', Object.entries(QUEUE_TYPES))}
      {select('severity', 'Severity', [['critical', 'Critical'], ['warning', 'Warning']])}
      {select('acknowledged', 'Seen', [['yes', 'Seen'], ['no', 'Unseen']])}
    </div>
    <form className={classes.section} onSubmit={(event) => {
      event.preventDefault();
      const bad = Object.values(ids).some((v) => v !== '' && (!/^[1-9]\d*$/.test(v) || !Number.isSafeInteger(Number(v))));
      setInvalid(bad);
      if (!bad) queue.setFilters({ ...filters, ...ids, page: 1 });
    }}>
      {['order_id', 'restaurant_id'].map((key) => <TextField key={key} label={humanize(key)}
        value={ids[key]} onChange={(event) => setIds({ ...ids, [key]: event.target.value })}
        inputProps={{ 'aria-label': `Alert ${humanize(key)} filter`, inputMode: 'numeric' }}
        style={{ width: '35%', marginRight: 8 }} />)}
      <Button type="submit" size="small">Apply</Button>
      {invalid && <div role="alert">Enter positive whole-number IDs.</div>}
    </form>
    {status === 'loading' && <div className={classes.status} role="status">Loading alerts…</div>}
    {status === 'error' && <div className={classes.retry} role="alert">Alerts unavailable. <Button size="small" onClick={queue.refresh}>Retry</Button></div>}
    {data && <>
      <div className={classes.status}>{data.affected_order_count} orders · {data.issue_count} issues</div>
      {data.business_calendar.filter((b) => b.reason).map((b) =>
        <div className={classes.status} role="status" key={b.business_id}>Business #{b.business_id}: {humanize(b.reason)}</div>)}
      <table className={classes.table} aria-label="Delivery alerts">
        <thead><tr><th className={classes.th}>Order ID</th><th className={classes.th}>Restaurant</th><th className={classes.th}>Problem</th></tr></thead>
        <tbody>{data.results.map((group) => <tr key={`${group.business_id}:${group.order_id}`}>
          <td className={classes.cell}><button className={classes.orderButton}
            aria-label={`Open order ${group.order_id}`} onClick={(event) => {
              setSelected(group);
            }}>#{group.order_id}</button><span className={classes.secondary}>{group.issue_count} issues</span></td>
          <td className={classes.cell}>{queue.name(group.issues[0])}</td>
          <td className={classes.cell}>{group.issues.map((issue) => <div key={issue.alert_id}>
            <span className={issue.severity === 'critical' ? classes.critical : undefined}>{QUEUE_TYPES[issue.event_type]}</span>
            <span className={classes.secondary}>{issue.severity} · {seen[issue.alert_id] || issue.acknowledged_at ? 'Seen' : 'Unseen'}</span>
          </div>)}</td>
        </tr>)}</tbody>
      </table>
      {!data.results.length && <div className={classes.empty}>No open delivery alerts.</div>}
      <div className={classes.more}>
        <Button size="small" disabled={data.page <= 1} onClick={() => queue.setFilters({ ...filters, page: data.page - 1 })}>Previous</Button>
        <span>Page {data.page}</span>
        <Button size="small" disabled={!data.has_more || data.page >= 1000}
          onClick={() => queue.setFilters({ ...filters, page: data.page + 1 })}>Next</Button>
      </div>
      {data.diagnostic_order_count > 0 && <details className={classes.section}>
        <summary>Evaluation unavailable · {data.diagnostic_order_count} orders</summary>
        {data.diagnostic_orders.map((row) => <div key={`${row.business_id}:${row.order_id}`} className={classes.section}>
          <strong>Order #{row.order_id} · Business #{row.business_id}</strong>
          <EvidenceFields value={row.evaluation} />
        </div>)}
        {data.diagnostic_order_count > data.diagnostic_orders.length &&
          <span className={classes.secondary}>Showing {data.diagnostic_orders.length} of {data.diagnostic_order_count} orders</span>}
      </details>}
    </>}
  </div>;
}
